/**
 * Trial Banner Component
 *
 * Persistent banner rendered between nav and main content for trialing users.
 * Shows usage progress and warning/blocked states based on trial limits.
 * Self-gates: renders nothing if user is not trialing.
 */

import { AlertTriangle, Info, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { useSubscriptionStatus } from "../hooks/useBilling";

export function TrialBanner() {
  const { data: status } = useSubscriptionStatus();
  const navigate = useNavigate();

  // Only show for trialing users
  if (!status?.is_trialing) {
    return null;
  }

  const warningLevel = status.trial_warning_level ?? "none";

  // Calculate the highest usage percentage across all dimensions
  let maxPercentage = 0;

  if (
    status.trial_conversations_limit > 0 &&
    status.trial_conversations_used !== undefined
  ) {
    const pct =
      (status.trial_conversations_used / status.trial_conversations_limit) *
      100;
    if (pct > maxPercentage) maxPercentage = pct;
  }
  if (
    status.trial_total_cost_limit_usd !== null &&
    status.trial_total_cost_used_usd !== null &&
    status.trial_total_cost_limit_usd > 0
  ) {
    const pct =
      (status.trial_total_cost_used_usd / status.trial_total_cost_limit_usd) *
      100;
    if (pct > maxPercentage) maxPercentage = pct;
  }
  if (
    status.trial_llm_cost_limit_usd !== null &&
    status.trial_llm_cost_used_usd !== null &&
    status.trial_llm_cost_limit_usd > 0
  ) {
    const pct =
      (status.trial_llm_cost_used_usd / status.trial_llm_cost_limit_usd) * 100;
    if (pct > maxPercentage) maxPercentage = pct;
  }
  if (
    status.trial_transcription_cost_limit_usd !== null &&
    status.trial_transcription_cost_used_usd !== null &&
    status.trial_transcription_cost_limit_usd > 0
  ) {
    const pct =
      (status.trial_transcription_cost_used_usd /
        status.trial_transcription_cost_limit_usd) *
      100;
    if (pct > maxPercentage) maxPercentage = pct;
  }

  const clampedPercentage = Math.min(maxPercentage, 100);

  const handleSubscribe = () => {
    navigate("/account?tab=billing");
  };

  if (warningLevel === "blocked") {
    return (
      <div
        className="border-b border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800 px-4 py-2.5"
        role="alert"
        data-testid="trial-banner"
      >
        <div className="container mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <XCircle className="h-4 w-4 shrink-0" />
            <span className="text-sm font-medium">
              Trial limit reached — Subscribe to keep using AttuneWell
            </span>
          </div>
          <Button size="sm" variant="destructive" onClick={handleSubscribe}>
            Subscribe
          </Button>
        </div>
      </div>
    );
  }

  if (warningLevel === "warning") {
    return (
      <div
        className="border-b border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-2.5"
        role="alert"
        data-testid="trial-banner"
      >
        <div className="container mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="text-sm font-medium">
              Approaching trial limit ({Math.round(clampedPercentage)}% used) —
              Subscribe to continue
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-500 text-amber-700 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-950"
            onClick={handleSubscribe}
          >
            Subscribe
          </Button>
        </div>
      </div>
    );
  }

  // Default: subtle/neutral state (< 80%)
  return (
    <div
      className="border-b border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-900 px-4 py-2"
      data-testid="trial-banner"
    >
      <div className="container mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-blue-700 dark:text-blue-400">
          <Info className="h-4 w-4 shrink-0" />
          <span className="text-sm">
            Free trial — {Math.round(clampedPercentage)}% used
          </span>
          <div className="hidden sm:block h-1.5 w-24 rounded-full bg-blue-200 dark:bg-blue-900">
            <div
              className="h-full rounded-full bg-blue-500 dark:bg-blue-600 transition-all"
              style={{ width: `${clampedPercentage}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
